import PropTypes from "prop-types"

export const CALCULATION_STATUS = Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    SUCCESS: "success",
    ERROR: "error",
})

const EMPTY_RESULTS = Object.freeze({
    puparCalculado: null,
    deltaPupar: null,
    variacaoPercentualPupar: null,
    pumtmCalculado: null,
    deltaPumtm: null,
    variacaoPercentualPumtm: null,
    spreadEmissao: null,
    prazoMedioEmissao: null,
    percentualPupar: null,
    durationModificada: null,
    durationMacaulay: null,
})

const EMPTY_PREMIO = Object.freeze({
    aplicavel: false,
    valor: null,
    memoria: [],
})

const getMemoryRows = (rows) => (Array.isArray(rows) ? rows : [])

const normalizePremio = (premio) => {
    const source = premio ?? {}

    return {
        ...EMPTY_PREMIO,
        ...source,
        aplicavel: Boolean(source.aplicavel),
        memoria: getMemoryRows(source.memoria),
    }
}

const normalizeMemories = (memories) => {
    const source = memories ?? {}

    return {
        ...source,
        pupar: getMemoryRows(source.pupar),
        pumtm: getMemoryRows(source.pumtm),
        fatorCorrecao: getMemoryRows(source.fatorCorrecao),
    }
}

export const createInitialValidationParameters = () => ({
    dataReferencia: "",
    puparReferencia: "",
    pumtmReferencia: "",
    spreadCredito: "",
})

export const getValidationViewModel = (viewModel) => {
    const source = viewModel ?? {}

    return {
        results: {...EMPTY_RESULTS, ...(source.results || {})},
        premios: {
            recompra: normalizePremio(source.premios?.recompra),
            conversibilidade: normalizePremio(source.premios?.conversibilidade),
        },
        memories: normalizeMemories(source.memories),
    }
}

export const mapSavedValidation = (data) => ({
    parameters: {
        puparReferencia: String(data.PUPARReferencia ?? ""),
        pumtmReferencia: String(data.PUMTMReferencia ?? ""),
        spreadCredito: String(data.SpreadCredito ?? ""),
    },
    viewModel: getValidationViewModel({
        results: {
            puparCalculado: data.PUPARCalculado ?? null,
            deltaPupar: data.DeltaPUPAR ?? null,
            variacaoPercentualPupar: data.VariacaoPercentualPUPAR ?? null,
            pumtmCalculado: data.PUMTMCalculado ?? null,
            deltaPumtm: data.DeltaPUMTM ?? null,
            variacaoPercentualPumtm: data.VariacaoPercentualPUMTM ?? null,
            spreadEmissao: data.SpreadEmissao ?? null,
            prazoMedioEmissao: data.PrazoMedioEmissao ?? null,
            percentualPupar: data.PercentualPUPAR ?? null,
            durationModificada: data.DurationModificada ?? null,
            durationMacaulay: data.DurationMacaulay ?? null,
        },
        premios: {
            recompra: {
                aplicavel: data.PremioRecompra != null,
                valor: data.PremioRecompra ?? null,
            },
            conversibilidade: {
                aplicavel: data.PremioConversibilidade != null,
                valor: data.PremioConversibilidade ?? null,
            },
        },
    }),
})

const toValidationApiValue = (value) => (
    value === "" || value === null || value === undefined
        ? null
        : value
)

const getPersistedDurationMacaulay = (value) => (
    value !== null && typeof value === "object"
        ? toValidationApiValue(value.Dias)
        : toValidationApiValue(value)
)

export const buildValidationPayload = ({
    parameters,
    results,
    premios,
}) => ({
    DataReferencia: parameters.dataReferencia,
    PUPARReferencia: toValidationApiValue(parameters.puparReferencia),
    PUMTMReferencia: toValidationApiValue(parameters.pumtmReferencia),
    SpreadCredito: toValidationApiValue(parameters.spreadCredito),

    PUPARCalculado: toValidationApiValue(results.puparCalculado),
    DeltaPUPAR: toValidationApiValue(results.deltaPupar),
    VariacaoPercentualPUPAR: toValidationApiValue(
        results.variacaoPercentualPupar
    ),

    PUMTMCalculado: toValidationApiValue(results.pumtmCalculado),
    DeltaPUMTM: toValidationApiValue(results.deltaPumtm),
    VariacaoPercentualPUMTM: toValidationApiValue(
        results.variacaoPercentualPumtm
    ),

    SpreadEmissao: toValidationApiValue(results.spreadEmissao),
    PrazoMedioEmissao: toValidationApiValue(results.prazoMedioEmissao),
    PremioRecompra: toValidationApiValue(premios.recompra.valor),
    PremioConversibilidade: toValidationApiValue(
        premios.conversibilidade.valor
    ),
    PercentualPUPAR: toValidationApiValue(results.percentualPupar),
    DurationModificada: toValidationApiValue(results.durationModificada),
    DurationMacaulay: getPersistedDurationMacaulay(results.durationMacaulay),
})

const memoryRowsPropType = PropTypes.arrayOf(PropTypes.object)

export const validationResultsPropType = PropTypes.shape({
    puparCalculado: PropTypes.any,
    deltaPupar: PropTypes.any,
    variacaoPercentualPupar: PropTypes.any,
    pumtmCalculado: PropTypes.any,
    deltaPumtm: PropTypes.any,
    variacaoPercentualPumtm: PropTypes.any,
    spreadEmissao: PropTypes.any,
    prazoMedioEmissao: PropTypes.any,
    percentualPupar: PropTypes.any,
    durationModificada: PropTypes.any,
    durationMacaulay: PropTypes.any,
})

export const validationPremioPropType = PropTypes.shape({
    aplicavel: PropTypes.bool,
    valor: PropTypes.any,
    memoria: memoryRowsPropType,
})

export const validationPremiosPropType = PropTypes.shape({
    recompra: validationPremioPropType,
    conversibilidade: validationPremioPropType,
})

export const validationMemoriesPropType = PropTypes.shape({
    pupar: memoryRowsPropType,
    pumtm: memoryRowsPropType,
    fatorCorrecao: memoryRowsPropType,
})

const normalizedValidationPremioPropType = PropTypes.shape({
    aplicavel: PropTypes.bool.isRequired,
    valor: PropTypes.any,
    memoria: memoryRowsPropType.isRequired,
})

export const normalizedValidationPremiosPropType = PropTypes.shape({
    recompra: normalizedValidationPremioPropType.isRequired,
    conversibilidade: normalizedValidationPremioPropType.isRequired,
})

export const normalizedValidationMemoriesPropType = PropTypes.shape({
    pupar: memoryRowsPropType.isRequired,
    pumtm: memoryRowsPropType.isRequired,
    fatorCorrecao: memoryRowsPropType.isRequired,
})

export const validationViewModelPropType = PropTypes.shape({
    results: validationResultsPropType,
    premios: validationPremiosPropType,
    memories: validationMemoriesPropType,
})

export const validationContextPropType = PropTypes.shape({
    cetip: PropTypes.string,
    versaoDocumento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idCaracteristica: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    caracteristica: PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        CETIP: PropTypes.string,
        VersaoDocumento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
})

export const validationSnapshotPropType = PropTypes.shape({
    IDCaracteristica: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ParametrosValidacao: PropTypes.object,
    Caracteristica: PropTypes.object,
    Parametros: PropTypes.object,
    EventosOrdinarios: PropTypes.arrayOf(PropTypes.object),
    EventosExtraordinarios: PropTypes.arrayOf(PropTypes.object),
    RecomprasProgramadas: PropTypes.arrayOf(PropTypes.object),
    DatasAniversario: PropTypes.arrayOf(PropTypes.object),
    Premios: PropTypes.array,
    Conversibilidades: PropTypes.array,
})
